/**
 * Single source of truth for the pick-up / drop-off field copy (1NOW-1662).
 *
 * Two problems this replaces:
 *
 * 1. `desc` — the input placeholder — defaulted to "Where are you going?" in 15
 *    places. That's a destination prompt inherited from a travel-booking
 *    template. On a rental site the renter is choosing where to collect and
 *    return the car, not where they're travelling to.
 *
 * 2. `placeHolder` — the bold heading — was written out at each call site, so
 *    the same field read "Pick up Location", "Pick Up Location", "Pickup
 *    Location", "Location" and "Dropoff Location" across one booking flow, and
 *    the desktop and mobile bars disagreed across the breakpoint (1NOW-1653).
 *
 * Import from here rather than typing the strings again — that's what stopped
 * them staying in sync last time.
 */
/**
 * Kept short on purpose. These sit beside the date and time fields, which use
 * terse labels over terse values ("From" / "Date", "Until" / "07:00"). Longer
 * prompts — an earlier attempt used "Where do you want to pick up and return?"
 * — overflow the field and truncate mid-word, and read nothing like the
 * columns next to them.
 */
export const LOCATION_COPY = {
  /** Pick-up field when drop-off is entered separately. */
  pickUp: {
    label: "Pick-up",
    prompt: "Select Location",
  },
  /** Drop-off field. */
  dropOff: {
    label: "Drop-off",
    prompt: "Select Location",
  },
  /** One field covering both, i.e. returning to the same place. */
  same: {
    label: "Location",
    prompt: "Select Location",
  },
} as const;

/** Toggle copy for the same/different drop-off switch. Kept here for the same reason. */
export const DROP_OFF_TOGGLE = {
  same: "Same drop-off",
  different: "Different drop-off",
} as const;

/** Date / time field labels. Same drift, same fix (1NOW-1653). */
export const DATE_TIME_COPY = {
  dateRange: "Pick-up & drop-off date",
  timeRange: "Pick-up - drop-off",
  /** Empty-state prompt, mirroring the location field's "Select Location". */
  datePlaceholder: "Select Dates",
} as const;
